import { inject, Injectable } from '@angular/core';
import {
    Assignment,
    AssignmentGroup,
    AssignmentTypes,
    CONFIG_TOKEN,
    isAssignmentAppointmentDetailsModel,
    isAssignmentGroupModel,
    isAssignmentMedicationDetailsModel,
    isAssignmentModel,
    isAssignmentPatientDetailsModel,
    isAssignmentPlanDetailsModel,
    isAssignmentVeterinarianDetailsModel,
    isPlanAppointmentDetailsModel,
    isPlanMedicationDetailsModel,
    isPlanPatientDetailsModel,
    isPlanVeterinarianDetailsModel,
    Plan,
    PlanTypes,
    ReferenceDataForPlans,
} from '@pxl/models/common';
import {
    AppointmentService,
    AssignmentPlanService,
    CommonService,
    LoggingService,
    LoginService,
    MedicationService,
    PatientService,
    VeterinarianService,
} from '@pxl/models/contracts';
import {
    AssignmentGroupModel,
    AssignmentGroupService as AssignmentGroupBackendService,
    AssignmentGroupUpdateModel,
    AssignmentModel,
    AssignmentService as AssignmentBackendService,
    AssignmentTypeModel,
    AssignmentTypeService as AssignmentTypeBackendService,
    AssignmentUpdateModel,
    AssignmentViewModelBase,
    IPlanSearchQueryModel,
    PlanCreateOrUpdateModel,
    PlanModel,
    PlanService as PlanBackendService,
} from '@pxl/shared/generated';
import { firstOrNull } from '@pxl/shared/util';
import { firstValueFrom, Observable, of, zip } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';

type ModelType<T> = { models: T[] } & ReferenceDataForPlans;
type ModelIds = {
    vetIds: Set<string>;
    medicationIds: Set<string>;
    appointmentIds: Set<string>;
    patientIds: Set<string>;
    planIds: Set<string>;
};

function extractPlanIds(models: PlanModel[] = []): ModelIds {
    const vetIds = new Set<string>();
    const medicationIds = new Set<string>();
    const appointmentIds = new Set<string>();
    const patientIds = new Set<string>();
    const planIds = new Set<string>();

    models?.forEach((model) => {
        model.additionalDetails.forEach((details) => {
            if (isPlanVeterinarianDetailsModel(details)) {
                vetIds.add(details.veterinarianId);
            } else if (isPlanAppointmentDetailsModel(details)) {
                appointmentIds.add(details.appointmentId);
            } else if (isPlanMedicationDetailsModel(details)) {
                medicationIds.add(details.medicationId);
            } else if (isPlanPatientDetailsModel(details)) {
                patientIds.add(details.patientId);
            }
        });

        model?.assignments?.forEach((assignment) => {
            assignment?.additionalDetails?.forEach((details) => {
                if (isAssignmentAppointmentDetailsModel(details)) {
                    appointmentIds.add(details.appointmentId);
                }
            });
        });
    });

    return {
        appointmentIds,
        medicationIds,
        patientIds,
        planIds,
        vetIds,
    };
}

function extractAssignmentGroupsIds(models: AssignmentGroupModel[]): ModelIds {
    const vetIds = new Set<string>();
    const medicationIds = new Set<string>();
    const appointmentIds = new Set<string>();
    const patientIds = new Set<string>();
    const planIds = new Set<string>();

    models.forEach((model) => {
        const ids = extractAssignmentIds(model.assignments);
        ids.vetIds.forEach((val) => vetIds.add(val));
        ids.planIds.forEach((val) => planIds.add(val));
        ids.patientIds.forEach((val) => patientIds.add(val));
        ids.medicationIds.forEach((val) => medicationIds.add(val));
        ids.appointmentIds.forEach((val) => appointmentIds.add(val));
    });

    return {
        appointmentIds,
        medicationIds,
        patientIds,
        planIds,
        vetIds,
    };
}

function extractAssignmentIds(models: AssignmentModel[]): ModelIds {
    const vetIds = new Set<string>();
    const medicationIds = new Set<string>();
    const appointmentIds = new Set<string>();
    const patientIds = new Set<string>();
    const planIds = new Set<string>();

    models?.forEach((model) =>
        model?.additionalDetails?.forEach((details) => {
            if (isAssignmentVeterinarianDetailsModel(details)) {
                vetIds.add(details.veterinarianId);
            } else if (isAssignmentAppointmentDetailsModel(details)) {
                appointmentIds.add(details.appointmentId);
            } else if (isAssignmentMedicationDetailsModel(details)) {
                medicationIds.add(details.medicationId);
            } else if (isAssignmentPatientDetailsModel(details)) {
                patientIds.add(details.patientId);
            } else if (isAssignmentPlanDetailsModel(details)) {
                planIds.add(details.planId);
            }
        }),
    );

    return {
        appointmentIds,
        medicationIds,
        patientIds,
        planIds,
        vetIds,
    };
}

function toPlans({
    models,
    appointments,
    medicationRoutesOfAdministration,
    medicationUnits,
    medications,
    patients,
    veterinarians,
    assignmentTypes,
}: ModelType<PlanModel>): Plan[] {
    return models?.map((model) => {
        return new Plan({
            ...model,
            appointments,
            veterinarians,
            patients,
            assignmentTypes,
            medications,
            medicationUnits,
            medicationRoutesOfAdministration,
        });
    });
}

function toAssignments({
    models,
    appointments,
    medicationRoutesOfAdministration,
    medicationUnits,
    medications,
    patients,
    veterinarians,
    assignmentTypes,
    plans,
}: ModelType<AssignmentModel>): Assignment[] {
    return models?.map((model) => {
        return new Assignment({
            ...model,
            appointments,
            veterinarians,
            patients,
            assignmentTypes,
            medications,
            medicationUnits,
            medicationRoutesOfAdministration,
            plans,
        });
    });
}

function toAssignmentGroups({
    models,
    appointments,
    medicationRoutesOfAdministration,
    medicationUnits,
    medications,
    patients,
    veterinarians,
    assignmentTypes,
}: ModelType<AssignmentGroupModel>): AssignmentGroup[] {
    return models?.map((model) => {
        return new AssignmentGroup({
            ...model,
            appointments,
            veterinarians,
            patients,
            assignmentTypes,
            medications,
            medicationUnits,
            medicationRoutesOfAdministration,
        });
    });
}

@Injectable({
    providedIn: 'root',
})
export class DefaultAssignmentPlanService implements AssignmentPlanService {
    private assignmentTypeModels$: Observable<AssignmentTypeModel[]>; // some kind of caching? should be removed?

    protected readonly assignmentTypeBackendService = inject(AssignmentTypeBackendService);
    protected readonly assignmentBackendService = inject(AssignmentBackendService);
    protected readonly planBackendService = inject(PlanBackendService);
    protected readonly assignmentGroupBackendService = inject(AssignmentGroupBackendService);
    protected readonly loginService = inject(LoginService);
    protected readonly veterinarianService = inject(VeterinarianService);
    protected readonly patientService = inject(PatientService);
    protected readonly appointmentService = inject(AppointmentService);
    protected readonly medicationService = inject(MedicationService);
    protected readonly commonService = inject(CommonService);
    protected readonly config = inject(CONFIG_TOKEN);
    protected readonly loggingService = inject(LoggingService);

    protected get apiVersion(): string {
        return this.config.API_VERSION;
    }

    protected get token(): string {
        return this.loginService.getToken();
    }

    /****************
     * Plans API
     ****************/
    public getHealthPlans(patientId: string, includeArchived = false, includeHidden = true): Promise<Plan[]> {
        return this.getPlans([PlanTypes.TherapyPlan, PlanTypes.TreatmentPlan], [patientId], includeArchived, includeHidden);
    }

    public getPlansByIds(planIds: string[], prefetchedReferences?: ReferenceDataForPlans): Promise<Plan[]> {
        return this.createPlanInstances(this.planBackendService.getPlansByIds(planIds, this.apiVersion, this.token), prefetchedReferences);
    }

    public hidePlan(planId: string): Promise<void> {
        return firstValueFrom(this.planBackendService.hidePlan(planId, true, this.apiVersion, this.token));
    }

    public showPlan(planId: string): Promise<void> {
        return firstValueFrom(this.planBackendService.hidePlan(planId, false, this.apiVersion, this.token));
    }

    public async createAssignmentPlan(
        planCreateModel: PlanCreateOrUpdateModel,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Plan> {
        return firstOrNull(
            await this.createPlanInstances(
                this.planBackendService.createPlan(planCreateModel, this.apiVersion, this.token).pipe(
                    first(),
                    map((plan) => [plan]),
                ),
                prefetchedReferences,
            ),
        );
    }

    public async updateAssignmentPlan(
        planId: string,
        planCreateModel: PlanCreateOrUpdateModel,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Plan> {
        return firstOrNull(
            await this.createPlanInstances(
                this.planBackendService.updatePlan(planCreateModel, this.apiVersion, this.token, planId).pipe(
                    first(),
                    map((plan) => [plan]),
                ),
                prefetchedReferences,
            ),
        );
    }

    public archiveAssignmentPlan(planId: string): Promise<void> {
        return firstValueFrom(this.planBackendService.archivePlan(planId, this.apiVersion, this.token));
    }

    protected getPlans(
        planTypes: string[],
        patientId: string[],
        includeArchived = false,
        includeHidden = true,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Plan[]> {
        return this.createPlanInstances(
            this.planBackendService.getPlans(this.apiVersion, this.token, planTypes, patientId, includeArchived, includeHidden),
            prefetchedReferences,
        );
    }

    protected searchPlans(searchQuery: IPlanSearchQueryModel, prefetchedReferences?: ReferenceDataForPlans) {
        return this.createPlanInstances(
            this.planBackendService.searchPlans(searchQuery, this.apiVersion, this.token),
            prefetchedReferences,
        );
    }

    private async createPlanInstances(obs: Observable<PlanModel[]>, prefetchedReferences?: ReferenceDataForPlans): Promise<Plan[]> {
        return firstValueFrom(this.createInstances(obs, extractPlanIds, toPlans, prefetchedReferences));
    }

    /****************
     * Assignments API
     ****************/
    public fetchAssignmentTypeModels(): Observable<AssignmentTypeModel[]> {
        if (!this.assignmentTypeModels$) {
            this.assignmentTypeModels$ = this.assignmentTypeBackendService
                .getAssignmentTypes(this.apiVersion, this.token)
                .pipe(shareReplay(1));
        }
        return this.assignmentTypeModels$;
    }

    public updateAssignmentsInGroup(assignmentGroupUpdateModels: AssignmentGroupUpdateModel[]): Promise<void> {
        return firstValueFrom(
            this.assignmentGroupBackendService.updateAssignmentsInGroup(assignmentGroupUpdateModels, this.apiVersion, this.token),
        );
    }

    public performAssignment(assignmentId: string, assignmentPerformedDateUtc?: Date | null): Promise<void> {
        return firstValueFrom(
            this.assignmentBackendService.performAssignment(assignmentId, this.apiVersion, this.token, assignmentPerformedDateUtc),
        );
    }

    public getAssignmentList(
        assignmentTypes: AssignmentTypes[],
        patientIds: string[] = [],
        startDate?: Date,
    ): Promise<(Assignment | AssignmentGroup)[]> {
        return this.createAssignmentAndAssignmentGroupInstances(
            this.assignmentBackendService.getAssignmentList(this.apiVersion, this.token, assignmentTypes, patientIds, startDate),
        );
    }

    public updateAssignmentsInPlan(planId: string, assignmentUpdateModels: AssignmentUpdateModel[]): Promise<void> {
        return firstValueFrom(this.planBackendService.updateAssignmentsInPlan(assignmentUpdateModels, this.apiVersion, this.token, planId));
    }

    public getAssignmentsByIds(assignmentIds: string[]): Promise<Assignment[]> {
        return this.createAssignmentInstances(
            this.assignmentBackendService.getAssignmentsByIds(assignmentIds, this.apiVersion, this.token),
        );
    }

    public getAssignmentsGroupsByIds(assignmentsGroupIds: string[]): Promise<AssignmentGroup[]> {
        return this.createAssignmentGroupInstances(
            this.assignmentGroupBackendService.getAssignmentGroupsByIds(assignmentsGroupIds, this.apiVersion, this.token),
        );
    }

    public getAssignmentGroups(
        patientId: string,
        assignmentTypeIds: string[],
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<AssignmentGroup[]> {
        return this.createAssignmentGroupInstances(
            this.assignmentGroupBackendService.getAssignmentGroups(this.apiVersion, this.token, assignmentTypeIds, [patientId]),
            prefetchedReferences,
        );
    }

    private async createAssignmentAndAssignmentGroupInstances(
        obs: Observable<AssignmentViewModelBase[]>,
    ): Promise<(Assignment | AssignmentGroup)[]> {
        const resultObs = obs.pipe(
            switchMap((assignmentViewModels: AssignmentViewModelBase[]) => {
                const assignmentModels: AssignmentModel[] = [];
                const assignmentGroupModels: AssignmentGroupModel[] = [];

                assignmentViewModels?.forEach((assignmentViewModel) => {
                    if (isAssignmentModel(assignmentViewModel)) {
                        assignmentModels.push(assignmentViewModel);
                    } else if (isAssignmentGroupModel(assignmentViewModel)) {
                        assignmentGroupModels.push(assignmentViewModel);
                    }
                });

                return zip(
                    this.createAssignmentInstances(of(assignmentModels)),
                    this.createAssignmentGroupInstances(of(assignmentGroupModels)),
                );
            }),
            switchMap((values) => of([...values[0], ...values[1]])),
        );

        return firstValueFrom(resultObs);
    }

    private async createAssignmentInstances(
        obs: Observable<AssignmentModel[]>,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Assignment[]> {
        return firstValueFrom(this.createInstances(obs, extractAssignmentIds, toAssignments, prefetchedReferences));
    }

    private async createAssignmentGroupInstances(
        obs: Observable<AssignmentGroupModel[]>,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<AssignmentGroup[]> {
        return firstValueFrom(this.createInstances(obs, extractAssignmentGroupsIds, toAssignmentGroups, prefetchedReferences));
    }

    /**
     * @name createInstances
     * @description creates instances of Plan[] from an Observable<PlanModel[]>
     */
    private createInstances(
        obs: Observable<PlanModel[]>,
        extractIds: (models: PlanModel[]) => ModelIds,
        toModel: (data: ModelType<PlanModel>) => Plan[],
        prefetchedReferences?: ReferenceDataForPlans,
    ): Observable<Plan[]>;

    /**
     * @name createInstances
     * @description creates instances of Assignment[] from an Observable<AssignmentModel[]>
     */
    private createInstances(
        obs: Observable<AssignmentModel[]>,
        extractIds: (models: AssignmentModel[]) => ModelIds,
        toModel: (data: ModelType<AssignmentModel>) => Assignment[],
        prefetchedReferences?: ReferenceDataForPlans,
    ): Observable<Assignment[]>;

    /**
     * @name createInstances
     * @description creates instances of AssignmentGroup[] from an Observable<AssignmentGroupModel[]>
     */
    private createInstances(
        obs: Observable<AssignmentGroupModel[]>,
        extractIds: (models: AssignmentGroupModel[]) => ModelIds,
        toModel: (data: ModelType<AssignmentGroupModel>) => AssignmentGroup[],
        prefetchedReferences?: ReferenceDataForPlans,
    ): Observable<AssignmentGroup[]>;

    private createInstances<
        TIn extends PlanModel[] | AssignmentModel[] | AssignmentGroupModel[],
        TOut extends Plan[] | AssignmentGroup[] | Assignment[],
    >(
        obs: Observable<TIn>,
        extractIds: (models: TIn) => ModelIds,
        toModel: (data: { models: TIn } & ReferenceDataForPlans) => TOut,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Observable<TOut> {
        return obs.pipe(
            switchMap(async (models) => {
                const { appointmentIds, medicationIds, patientIds, planIds, vetIds } = extractIds(models);

                this.removePrefetchedIds({ appointmentIds, medicationIds, patientIds, planIds, vetIds }, prefetchedReferences);

                const promises = this.createDataPromises(
                    { appointmentIds, medicationIds, patientIds, planIds, vetIds },
                    prefetchedReferences,
                );

                const [
                    veterinarians,
                    patients,
                    appointments,
                    assignmentTypes,
                    medications,
                    medicationUnits,
                    medicationRoutesOfAdministration,
                    plans,
                ] = await Promise.all(promises);

                return {
                    models,
                    veterinarians,
                    patients,
                    appointments,
                    assignmentTypes,
                    medications,
                    medicationUnits,
                    medicationRoutesOfAdministration,
                    plans,
                };
            }),
            map(toModel),
        );
    }

    private removePrefetchedIds(ids: ModelIds, prefetchedReferences?: ReferenceDataForPlans): void {
        prefetchedReferences?.appointments?.forEach((appointment) => {
            ids.appointmentIds.delete(appointment.id);
        });
        prefetchedReferences?.veterinarians?.forEach((veterinarian) => {
            ids.vetIds.delete(veterinarian.id);
        });
        prefetchedReferences?.medications?.forEach((medication) => {
            ids.medicationIds.delete(medication.id);
        });
        prefetchedReferences?.patients?.forEach((patient) => {
            ids.patientIds.delete(patient.patientId);
        });
        prefetchedReferences?.plans?.forEach((plan) => {
            ids.planIds.delete(plan.id);
        });
    }

    private createDataPromises(ids: ModelIds, prefetchedReferences?: ReferenceDataForPlans): Promise<any>[] {
        const veterinariansPromise = this.createVeterinariansPromise(ids.vetIds, prefetchedReferences);
        const appointmentsPromise = this.createAppointmentsPromise(ids.appointmentIds, prefetchedReferences);
        const patientsPromise = this.createPatientsPromise(ids.patientIds, prefetchedReferences);
        const assignmentTypesPromise = firstValueFrom(this.fetchAssignmentTypeModels());
        const medicationsPromise = this.createMedicationsPromise(ids.medicationIds, prefetchedReferences);
        const planPromise = this.createPlanPromise(ids.planIds, prefetchedReferences);
        const { medicationUnitsPromise, medicationRoutesPromise } = this.createMedicationMetadataPromises(prefetchedReferences);

        return [
            veterinariansPromise,
            patientsPromise,
            appointmentsPromise,
            assignmentTypesPromise,
            medicationsPromise,
            medicationUnitsPromise,
            medicationRoutesPromise,
            planPromise,
        ];
    }

    private createVeterinariansPromise(vetIds: Set<string>, prefetchedReferences?: ReferenceDataForPlans) {
        return vetIds.size > 0
            ? this.veterinarianService
                  .getVeterinariansById([...vetIds])
                  .then((vets) => [...vets, ...(prefetchedReferences?.veterinarians || [])])
            : Promise.resolve(prefetchedReferences?.veterinarians);
    }

    private createAppointmentsPromise(appointmentIds: Set<string>, prefetchedReferences?: ReferenceDataForPlans) {
        return appointmentIds.size > 0
            ? firstValueFrom(this.appointmentService.getAppointments({ filterAppointmentIds: [...appointmentIds] })).then(
                  (appointments) => [...appointments, ...(prefetchedReferences?.appointments || [])],
              )
            : Promise.resolve(prefetchedReferences?.appointments);
    }

    private createPatientsPromise(patientIds: Set<string>, prefetchedReferences?: ReferenceDataForPlans) {
        return patientIds.size > 0
            ? this.patientService
                  .getPatientsWithFilter([...patientIds])
                  .then((patients) => [...patients, ...(prefetchedReferences?.patients || [])])
            : Promise.resolve(prefetchedReferences?.patients);
    }

    private createMedicationsPromise(medicationIds: Set<string>, prefetchedReferences?: ReferenceDataForPlans) {
        return medicationIds.size > 0
            ? firstValueFrom(this.medicationService.getMedicationsByIds([...medicationIds])).then((medications) => [
                  ...medications,
                  ...(prefetchedReferences?.medications || []),
              ])
            : Promise.resolve(prefetchedReferences?.medications);
    }

    private createPlanPromise(planIds: Set<string>, prefetchedReferences?: ReferenceDataForPlans) {
        return planIds.size > 0
            ? this.getPlansByIds([...planIds])
                  .then((plans) => [...plans, ...(prefetchedReferences?.plans || [])])
                  .catch((error) => {
                      this.loggingService.error('Error fetching plans by IDs:', error);
                      return prefetchedReferences?.plans || [];
                  })
            : Promise.resolve(prefetchedReferences?.plans || []);
    }

    private createMedicationMetadataPromises(prefetchedReferences?: ReferenceDataForPlans) {
        const medicationUnitsPromise = !prefetchedReferences?.medicationUnits?.length
            ? firstValueFrom(this.commonService.onMedicationUnits())
            : Promise.resolve(prefetchedReferences?.medicationUnits);

        const medicationRoutesPromise = !prefetchedReferences?.medicationRoutesOfAdministration?.length
            ? firstValueFrom(this.commonService.onMedicationRoutesOfAdministration())
            : Promise.resolve(prefetchedReferences?.medicationRoutesOfAdministration);

        return { medicationUnitsPromise, medicationRoutesPromise };
    }
}
